import React, { useEffect, useRef, useState } from 'react';

// Redux/Hooks/APIs/Utils
import {
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import { KeyValue } from '@/main/models/common';
import { useForm } from 'react-hook-form';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';
import {
  getCompanyManufacturer,
  useCompanyManufacturerReducer
} from '@/main/store/companyManufacturer';
import { getInsurers, useInsurerReducer } from '@/main/store/insurer';
import {
  getAffectedType,
  useAffectedTypeReducer
} from '@/main/store/affectedType';
import { CompanyManufacturerRequest } from '@/main/models/request/companyManufacturerRequest';
import { RateSaveRequest } from '@/main/models/request/rateRequest';
import { rateApi } from '@/main/api';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import { currencyToNumber, formatCurrency } from '@/main/services/utilsNumber';

// Components
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import KogInputPercentageControlled from '@/main/components/shared/inputPercentage/inputPercentage';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';

// Styles
import * as S from './styles';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';

interface IRateSidebar {
  open: boolean;
  closeSidebar: (refresh: boolean) => void;
  guidCompany: string | undefined | null;
  guidBodyShop: string | undefined | null;
}

export interface IRateForm {
  rateLabour: string | null;
  discountParts: number | null;
  ageStart: number | null;
  ageEnd: number | null;
  observations: string;
  manufacturers: KeyValue[] | null;
  insurers: KeyValue[] | null;
  affectedTypes: KeyValue[] | null;
}

const INITIAL_FORM_STATE: IRateForm = {
  ageStart: null,
  ageEnd: null,
  rateLabour: null as string | null,
  discountParts: null,
  observations: '',
  manufacturers: null,
  insurers: null,
  affectedTypes: null
};

function RateSidebar({
  open,
  closeSidebar,
  guidCompany,
  guidBodyShop
}: Readonly<IRateSidebar>) {
  // #region hooks
  const { companyManufacturer } = useCompanyManufacturerReducer();
  const { insurers: insurerReducer } = useInsurerReducer();
  const { affectedType } = useAffectedTypeReducer();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<IRateForm>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE
  });

  const dispatch = useDispatch();

  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
  // #endregion

  // #region state
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);

  // #endregion

  // #region functions
  function callApiDomainCompanyManufacturer() {
    if (isNullUndefined(guidCompany)) return;

    const request: CompanyManufacturerRequest = {
      companyGuid: guidCompany
    };

    dispatch(getCompanyManufacturer(request));
  }

  function callApiDomainInsurer() {
    if (isNullUndefined(guidBodyShop)) return;

    dispatch(getInsurers({ guidBodyShop: guidBodyShop }));
  }
  // #endregion

  // #region handlers
  const refIgnoreDuplicate = useRef<boolean>(false);
  const refChangeJustification = useRef<any>(null);

  async function onSubmitSave(data: IRateForm) {
    if (!guidCompany) return;

    if (Number(data.ageStart) > Number(data.ageEnd)) {
      setError('ageStart', {
        message: getTranslateByKey(
          'purchaseCriteria.endYearCannotBeLessThanStartYear',
          true
        )
      });
      setError('ageEnd', {
        message: getTranslateByKey(
          'purchaseCriteria.endYearCannotBeLessThanStartYear',
          true
        )
      });
      return;
    }

    const ignoreDuplicate = refIgnoreDuplicate.current;

    if (!ignoreDuplicate) {
      const resultConfirm = await getConfirmationReason(
        getTranslateByKey('information.confirmChangeTitle'),
        ReasonTypeEnum.ChangeCompany
      );

      if (!resultConfirm.confirmed) return;

      let filesRequest = undefined;
      if (resultConfirm.files && resultConfirm.files.length > 0) {
        filesRequest = await Promise.all(
          resultConfirm.files.map(async item => {
            const base64DataUrl = await toBase64(item);
            return {
              fileName: item.name,
              contentType: item.type,
              fileBase64: base64DataUrl as string
            };
          })
        );
      }

      refChangeJustification.current = {
        guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
        reasonDescription: resultConfirm?.observation || null,
        filesUpload: filesRequest
      };
    }

    const request: RateSaveRequest = removeEmptyValues({
      guidCompany: guidCompany,
      ageEnd: Number(data.ageEnd),
      ageStart: Number(data.ageStart),
      discountParts: Number(data.discountParts),
      guidmanufacturer: data.manufacturers
        ? data.manufacturers.map(item => String(item.key))
        : [],
      guidinsurers: data.insurers
        ? data.insurers.map(item => String(item.key))
        : [],
      idAffectedType: data.affectedTypes
        ? data.affectedTypes.map(item => Number(item.key))
        : [],
      observations: data.observations,
      rateLabour: currencyToNumber(data.rateLabour ?? ''),
      allowDuplicate: ignoreDuplicate,
      changeJustification: refChangeJustification.current
    });

    setIsLoadingSave(true);

    rateApi
      .save(request)
      .then(async res => {
        const result = res.data.result;

        if (result.duplicateRate) {
          const message = getTranslateByKey('information.confirmDuplicateRate');

          const isConfirmed = await getConfirmation(message);
          if (!isConfirmed) return;

          refIgnoreDuplicate.current = true;

          onSubmitSave(data);
        } else {
          closeSidebar(true);
          reset();

          toast.success(getTranslateByKey('information.rateSavedSucess'));
        }
      })
      .finally(() => {
        setIsLoadingSave(false);
        refIgnoreDuplicate.current = false;
        refChangeJustification.current = null;
      });
  }

  function onCloseModal() {
    reset();
    setIsLoadingForm(false);
    closeSidebar(false);
  }
  // #endregion

  // #region effects
  useEffect(() => {
    callApiDomainCompanyManufacturer();
    callApiDomainInsurer();
    dispatch(getAffectedType());
  }, []);

  // #endregion

  return (
    <>
      {open && (
        <KogModalSideComponent open={open} closeModal={onCloseModal}>
          <S.HeaderText>
            {getTranslateByKey('information.registerRate')}
          </S.HeaderText>
          <form onSubmit={handleSubmit(onSubmitSave)} noValidate>
            <S.Container>
              <KogInputAutoCompleteCheckControlled
                label={getTranslateByKey('information.insurer')}
                isLoading={insurerReducer.isLoading || isLoadingForm}
                selectAllLabel={getTranslateByKey('information.selectAll')}
                options={insurerReducer.data || []}
                control={control}
                name="insurers"
                required
                controllerProps={{
                  rules: {
                    required: true
                  }
                }}
                customInputProps={{
                  required: true,
                  id: 'id-rate-select-insurer'
                }}
              />
              <TooltipComponent
                title={
                  companyManufacturer.data?.length === 0
                    ? getTranslateByKey('information.tooltipAddMake')
                    : ''
                }>
                <div>
                  <KogInputAutoCompleteCheckControlled
                    label={getTranslateByKey('information.make')}
                    isLoading={companyManufacturer.isLoading || isLoadingForm}
                    selectAllLabel={getTranslateByKey('information.selectAll')}
                    options={companyManufacturer.data || []}
                    control={control}
                    name="manufacturers"
                    required
                    controllerProps={{
                      rules: {
                        required: true
                      }
                    }}
                    customInputProps={{
                      required: true,
                      id: 'id-rate-select-manufacturer'
                    }}
                  />
                </div>
              </TooltipComponent>
              <KogInputTextControlledNew
                name="ageStart"
                label={getTranslateByKey('information.ageStartYear')}
                hasOnlyNumberInteger
                control={control}
                disabled={isLoadingForm}
                controllerProps={{
                  rules: {
                    required: true
                  }
                }}
                inputProps={{ maxLength: 5 }}
                id="id-rate-txt-ageStart"
              />
              <KogInputTextControlledNew
                name="ageEnd"
                label={getTranslateByKey('information.ageEndYear')}
                hasOnlyNumberInteger
                control={control}
                disabled={isLoadingForm}
                controllerProps={{
                  rules: {
                    required: true
                  }
                }}
                inputProps={{ maxLength: 5 }}
                id="id-rate-txt-ageEnd"
              />
              <KogInputAutoCompleteCheckControlled
                label={getTranslateByKey('information.affectedType')}
                isLoading={affectedType.isLoading || isLoadingForm}
                disableSelectAll
                options={affectedType.data || []}
                control={control}
                name="affectedTypes"
                required
                controllerProps={{
                  rules: {
                    required: true
                  }
                }}
                customInputProps={{
                  required: true,
                  id: 'id-rate-select-affectedType'
                }}
              />
              <KogInputAmountControlled
                name="rateLabour"
                label={getTranslateByKey('information.rateLabour')}
                required
                control={control}
                typeInput={'filled'}
                id="id-rate-txt-rateLabour"
                customInputProps={{
                  disabled: isLoadingForm
                }}
              />
              <KogInputPercentageControlled
                name="discountParts"
                label={getTranslateByKey('information.percentDiscountParts')}
                control={control}
                customInputProps={{
                  disabled: isLoadingForm
                }}
                required
                idScreen="id-rate"
              />
              <KogInputTextControlledNew
                name="observations"
                label={getTranslateByKey('information.observation')}
                control={control}
                disabled={isLoadingForm}
                id="id-rate-txt-observations"
              />
            </S.Container>
            <S.AreaButton>
              <SecondaryButton
                id="id-rate-btn-save"
                type="submit"
                isLoading={isLoadingSave}>
                {getTranslateByKey('information.save')}
              </SecondaryButton>
            </S.AreaButton>
          </form>
        </KogModalSideComponent>
      )}
      <ModalConfirmation />
      <ModalConfirmationReason closeByOverlay={false} />
    </>
  );
}

export default RateSidebar;
